import React from 'react';
import PropTypes from 'prop-types';
import formatMessage from 'format-message';
import classnames from 'classnames';
import _ from 'lodash';

import AnalyticsActions from '../../actions/analytics';
import Message from '../common/message';
import ComplexMessage from '../common/complex-message';
import FavoriteResourceCard from '../resources/favorite-resource-card';
import { LoadingSpinner } from '../common/loading-spinner';
import { getState } from '../../store';
import AlertFooterImportStatus from '../alert-footer/alert-footer-import-status';
import LoadMore from '../common/load-more';
import Link from '../common/link';
import { calculatePaginationPages } from '../../common/util/calculate-pagination-pages';
import { isResourcePaginated } from '../../common/util/is-resource-paginated';
import RouterStore from '../../stores/router';
import ResultPagination from '../common/result-pagination';
import SearchActions from '../../actions/search';

export default class FavoriteResourcesList extends React.Component {
  static propTypes = {
    baseParams: PropTypes.object,
    fetchResults: PropTypes.func.isRequired,
    resultsPerPage: PropTypes.number.isRequired,
    results: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    emptyMessage: PropTypes.node.isRequired
  };

  constructor (props) {
    super(props);
    const params = this.createParamsFromProps(props);
    this.state = {
      isNewSearch: true,
      params,
    };
    this.isPaginatingToPreviousPage = false;
    this.onPageChange = this.onPageChange.bind(this);
    this.innerRef = React.createRef();
    this.endRef = React.createRef();
    this.props.fetchResults(params);
  }

  createParamsFromProps (props) {
    return {
      ...props.baseParams,
      ...props.router.query
    };
  }

  UNSAFE_componentWillReceiveProps (newProps) {
    if (this.state.isNewSearch && newProps.results.searchSuccessful) {
      this.setState({ isNewSearch: false });
      AnalyticsActions.viewedSearchResults(newProps.results.count);
    }

    if (this.props.router !== newProps.router) {
      const newParams = this.createParamsFromProps(newProps);

      this.props.fetchResults(newParams);
      this.setState({ isNewSearch: !newProps.router.query.page, params: newParams });
    }
  }

  componentDidUpdate () {
    if (this.scrollToTop && this.innerRef && this.innerRef.current) {
      this.innerRef.current.scrollTop = 0;
      this.scrollToTop = false;
      return;
    }

    if (this.isPaginatingToPreviousPage && !this.props.results.store.showLoading()) {
      this.scrollToBottom();
      this.isPaginatingToPreviousPage = false;
    }
  }

  scrollToBottom () {
    if (this.paginationScrollTop) {
      this.innerRef.current.scrollTop = this.paginationScrollTop;
      this.paginationScrollTop = null;
    } else {
      this.endRef.current.scrollIntoView();
    }
  }

  getInvisibleImportStatus = visibleResources => {
    const { importStatus } = this.props;
    const visibleIds = visibleResources.map(item => item.id);
    const ids = Object.keys(importStatus);
    const invisibleIds = _.difference(ids, visibleIds);

    return invisibleIds
      .map(id => ({ ...importStatus[id] }))
      .filter(status => status.isImporting || status.error || status.showSuccess);
  };

  getPaginationProps () {
    if (!isResourcePaginated()) {
      return { isPaginated: false };
    }

    const count = this.props.results.store.getFilteredResultsCount();
    if (!count) {
      return { isPaginated: true };
    }

    return {
      ...calculatePaginationPages(RouterStore.state.query.page, count, this.props.resultsPerPage),
      isPaginated: true
    };
  }

  onPageChange (currentPage, { isPreviousPageNavigation }) {
    this.isPaginatingToPreviousPage = isPreviousPageNavigation;

    const isPageFull = this.props.results.store.get().length === this.props.resultsPerPage;
    this.paginationScrollTop = (this.isPaginatingToPreviousPage && isPageFull) ? this.innerRef.current.scrollTop : null;

    SearchActions.update({ page: currentPage });
  }

  renderPagination (paginationProps, isEmpty) {
    if (isEmpty) {
      return null;
    }

    return <ResultPagination
      currentPage={paginationProps.currentPage}
      totalPages={paginationProps.totalPages}
      hasTooManyResults={paginationProps.hasTooManyResults}
      onPageChange={this.onPageChange}
    />;
  }

  renderLoadMore (isLoading, fetchResults) {
    return <LoadMore
      hasMore={this.props.results.hasMore}
      isLoading={isLoading}
      loadMore={() => {
        this.scrollLock = true;
        fetchResults();
      }}
    />;
  }

  render () {
    const {
      startImport,
      getImportStatusForResource,
      dismissErrorForResource,
    } = this.props;
    const isFavoritesTraySet = getState().featureFlags.flags.favorites_tray;
    const params = Object.assign(
      {},
      this.props.baseParams,
      this.props.router.query
    );
    const fetchResults = () => this.props.fetchResults(params, this.props.results.nextCursor);

    const isLoading = this.props.results.store.showLoading();

    if (isLoading) {
      const results = this.props.results.store.get();
      const invisbleImportStatus = this.getInvisibleImportStatus(results);
      return (
        <div className="AppHandler--loading">
          <LoadingSpinner />
          <AlertFooterImportStatus importStatus={invisbleImportStatus} onDismiss={dismissErrorForResource} />
        </div>
      );
    }

    const results = this.props.results.store.get();
    const invisbleImportStatus = this.getInvisibleImportStatus(results);

    const listClassnames = classnames({
      'FavoriteResources-list': !isFavoritesTraySet,
      'FavoriteResources-list-favoritesTray': isFavoritesTraySet,
      'FavoriteResources-list-loading': this.props.results.loading,
      'FavoriteResources-list-empty': !results.length
    });
    const rootClassNames = classnames(
      {
        FavoriteSearchResults: !isFavoritesTraySet,
        'FavoriteSearchResults-favoritesTray': isFavoritesTraySet
      },
      'SearchResult'
    );

    if (!this.isPaginatingToPreviousPage) {
      if (this.scrollLock !== true && !_.isEqual(this.lastResults, results)) {
        this.scrollToTop = true;
      } else if (this.scrollLock === true && !_.isEqual(this.lastResults, results)) {
        this.scrollLock = false;
      }
    }

    this.lastResults = results;
    const paginationProps = this.getPaginationProps();
    const isEmpty = !results.length;

    return (
      <div data-testid="favorite-resources-list" ref={this.innerRef} className={rootClassNames}>
        <ul className={listClassnames}>
          {!isEmpty
            ? results
              .map(learningObject => (
                <FavoriteResourceCard
                  startImport={startImport}
                  canvasUrl={this.props.session.canvasApiDomain}
                  courseId={this.props.session.canvasCourseId}
                  canvasResourceType={this.props.session.canvasResourceType}
                  courses={this.props.courses.courses}
                  key={learningObject.id}
                  importStatus={getImportStatusForResource(learningObject)}
                  dismissErrorForResource={dismissErrorForResource}
                  resource={learningObject}
                  returnUrl={this.props.session.returnUrl}
                />
              ))
            : this.props.emptyMessage}
          {/* The empty items ^ with zero height help align the last row
                of a flex-wrapped list */}
          {this.props.results.error && (
            <Message type="error2 message-with-icon">
              <ComplexMessage>
                {formatMessage(
                  'There was an error loading the results.\nYou can try again by {clicking_here}.',
                  { clicking_here: ComplexMessage.placeholder('retry') }
                )}
                <Link key="retry" onClick={fetchResults}>
                  {formatMessage('clicking here')}
                </Link>
              </ComplexMessage>
            </Message>
          )}
        </ul>
        <AlertFooterImportStatus importStatus={invisbleImportStatus} onDismiss={dismissErrorForResource} />
        {paginationProps.isPaginated
          ? this.renderPagination(paginationProps, isEmpty)
          : this.renderLoadMore(isLoading, fetchResults)}
        <div ref={this.endRef} />
      </div>
    );
  }
}
